import { type AdminLlmPromptTemplateSelectDto, LlmModel } from '@@types/api.types';
import type { AdminLlmPromptTemplateFormData } from '@schemas/admin-llm-prompt-template.schema';

const PLACEHOLDER_PATTERN = /{{\s*([a-z0-9_]+)\s*}}/g;
const DISPLAY_ACRONYMS = new Map([
  ['json', 'JSON'],
  ['llm', 'LLM'],
  ['mrl', 'MRL'],
  ['trl', 'TRL'],
]);

type PromptCategoryGroup = {
  category: string;
  label: string;
  prompts: AdminLlmPromptTemplateSelectDto[];
};

type PromptTemplateEditorPromptState = Pick<
  AdminLlmPromptTemplateSelectDto,
  'defaultModel' | 'resolvedModel' | 'supportsDefaultModelOverride' | 'template'
>;

const TITLE_PREFIX_BY_CATEGORY = new Map<string, string>([
  ['chat', 'Chat'],
  ['mission_assessment', 'Mission Assessment'],
  ['shared', 'Shared'],
  ['trl_analysis', 'TRL Analysis'],
  ['trl_projection', 'TRL Projection'],
]);

export function buildPromptTemplatePlaceholder(key: string): string {
  return `{{${key}}}`;
}

export function normalizePromptTemplateValue(template: string): string {
  return template.trim();
}

function resolvePromptTemplateDefaultModel(prompt: PromptTemplateEditorPromptState): LlmModel {
  return prompt.defaultModel ?? prompt.resolvedModel ?? LlmModel.OpenrouterAuto;
}

export function buildPromptTemplateEditorChangeState(params: {
  formData: AdminLlmPromptTemplateFormData;
  prompt?: PromptTemplateEditorPromptState;
}): {
  hasUnsavedChanges: boolean;
  isCreatingNewVersion: boolean;
} {
  const { formData, prompt } = params;

  if (!prompt) {
    return {
      hasUnsavedChanges: false,
      isCreatingNewVersion: false,
    };
  }

  const isCreatingNewVersion =
    normalizePromptTemplateValue(formData.template) !== normalizePromptTemplateValue(prompt.template);

  const hasModelChanges = prompt.supportsDefaultModelOverride
    ? formData.defaultModel !== resolvePromptTemplateDefaultModel(prompt)
    : false;

  return {
    hasUnsavedChanges: hasModelChanges || isCreatingNewVersion,
    isCreatingNewVersion,
  };
}

export function extractPromptTemplatePlaceholders(template: string): string[] {
  const matches = Array.from(template.matchAll(PLACEHOLDER_PATTERN));

  return Array.from(new Set(matches.map((match) => match[1])));
}

export function validatePromptTemplatePlaceholders(params: {
  allowedVariables: string[];
  template: string;
}): {
  unknownPlaceholders: string[];
  unusedVariables: string[];
  usedVariables: string[];
} {
  const { allowedVariables, template } = params;
  const extractedPlaceholders = extractPromptTemplatePlaceholders(template);
  const allowedVariableSet = new Set(allowedVariables);
  const usedVariableSet = new Set(extractedPlaceholders.filter((placeholder) => allowedVariableSet.has(placeholder)));

  return {
    unknownPlaceholders: extractedPlaceholders.filter((placeholder) => !allowedVariableSet.has(placeholder)),
    unusedVariables: allowedVariables.filter((variable) => !usedVariableSet.has(variable)),
    usedVariables: Array.from(usedVariableSet),
  };
}

export function insertPromptTemplatePlaceholderAtSelection(params: {
  placeholder: string;
  selectionEnd: number;
  selectionStart: number;
  template: string;
}): {
  nextCursorPosition: number;
  nextTemplate: string;
} {
  const { placeholder, selectionEnd, selectionStart, template } = params;
  const normalizedSelectionEnd = Math.max(selectionEnd, selectionStart);
  const normalizedSelectionStart = Math.min(selectionEnd, selectionStart);
  const nextTemplate = `${template.slice(0, normalizedSelectionStart)}${placeholder}${template.slice(normalizedSelectionEnd)}`;
  const nextCursorPosition = normalizedSelectionStart + placeholder.length;

  return {
    nextCursorPosition,
    nextTemplate,
  };
}

export function formatPromptCategoryLabel(category: string): string {
  if (category === 'shared') {
    return 'General';
  }

  return category
    .split('_')
    .filter(Boolean)
    .map((word) => {
      const normalizedWord = word.toLowerCase();

      return DISPLAY_ACRONYMS.get(normalizedWord) ?? `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(' ');
}

export function buildPromptCardDescription(prompt: AdminLlmPromptTemplateSelectDto): string | undefined {
  const description = prompt.description?.trim();

  return description ? description : undefined;
}

export function formatPromptListTitle(prompt: AdminLlmPromptTemplateSelectDto): string {
  const title = prompt.title.trim();
  const categoryPrefix = TITLE_PREFIX_BY_CATEGORY.get(prompt.category);

  if (!categoryPrefix) {
    return title;
  }

  const prefixedTitle = `${categoryPrefix} `;

  return title.startsWith(prefixedTitle) ? title.slice(prefixedTitle.length) : title;
}

export function groupPromptsByCategory(prompts: AdminLlmPromptTemplateSelectDto[]): PromptCategoryGroup[] {
  const groupedPrompts = new Map<string, AdminLlmPromptTemplateSelectDto[]>();

  for (const prompt of prompts) {
    const categoryPrompts = groupedPrompts.get(prompt.category) ?? [];
    categoryPrompts.push(prompt);
    groupedPrompts.set(prompt.category, categoryPrompts);
  }

  return Array.from(groupedPrompts.entries()).map(([category, categoryPrompts]) => ({
    category,
    label: formatPromptCategoryLabel(category),
    prompts: categoryPrompts,
  }));
}
